// Landing feature-showcase carousel (Landing::FeatureShowcaseComponent).
// Per-slide animation is split into strategies keyed by `data-slide-type` (see SLIDE_TYPES);
// this controller owns the shared rail: pill selection, auto-advance, progress ring, and
// viewport-triggered start.

import { Controller } from '@hotwired/stimulus';
import { gsap } from 'gsap';
import { underlineSweepBounds } from './feature_showcase_sweep';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Iris clip-paths keyed by card corner — opens from (and collapses toward) the owning card,
// so the motion reads as cause-and-effect.
const IRIS_OPEN_AT = {
  tr: 'circle(150% at 85% 15%)',
  bl: 'circle(150% at 15% 85%)',
  br: 'circle(150% at 85% 85%)',
  tl: 'circle(150% at 15% 15%)',
};
const IRIS_CLOSED_AT = {
  tr: 'circle(0% at 85% 15%)',
  bl: 'circle(0% at 15% 85%)',
  br: 'circle(0% at 85% 85%)',
  tl: 'circle(0% at 15% 15%)',
};

const CORNER_ROTATIONS = { tr: -3, tl: 5, bl: -6, br: 4 };

const MULTI_CARD_ARRIVAL_GAP = 2.8;

// Shimmer sweep drives two custom properties on the card element —
// --shimmer-progress (translateX %) and --shimmer-alpha (fade envelope).
// Styling lives in the landing SCSS on `.landing-hero-float--card .profile-card-content::before`.
const SHIMMER_DURATION = 0.9;
const SHIMMER_FADE_IN = 0.15;
const SHIMMER_FADE_OUT = 0.2;
const SHIMMER_START_X = -115;
const SHIMMER_END_X = 115;

const playShimmer = (tl, card, startAt) => {
  const state = { alpha: 0, progress: SHIMMER_START_X };
  const apply = () => {
    card.style.setProperty('--shimmer-alpha', state.alpha);
    card.style.setProperty('--shimmer-progress', `${state.progress}%`);
  };
  apply();
  tl.to(state, { alpha: 1, duration: SHIMMER_FADE_IN, ease: 'power2.out', onUpdate: apply }, startAt);
  tl.to(state, { progress: SHIMMER_END_X, duration: SHIMMER_DURATION, ease: 'power1.inOut', onUpdate: apply }, startAt);
  tl.to(state, { alpha: 0, duration: SHIMMER_FADE_OUT, ease: 'power2.in', onUpdate: apply }, startAt + SHIMMER_DURATION - SHIMMER_FADE_OUT);
};

// Card arrival, with a 1→1.04→1.0 flex beat so copy is legible before anything
// else lands on it. Used by every slide type that drops cards in.
const addCardArrival = (tl, card, t) => {
  const rot = CORNER_ROTATIONS[card.dataset.corner] ?? 0;
  tl.to(card, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.16, ease: 'power3.out' }, t);
  tl.to(card, { scale: 1.04, rotation: rot, duration: 0.14, ease: 'power2.out' }, t);
  tl.to(card, { scale: 1.0, duration: 0.12, ease: 'power2.inOut' }, '>');
};

// video.play() with silent rejection (autoplay can fail in background tabs).
const startVideoPlayback = (video) => {
  if (!video) return;
  const result = video.play();
  if (result && typeof result.catch === 'function') result.catch(() => {});
};

const CURSOR_TIP_X = 3;
const CURSOR_TIP_Y = 3;

// Cursor icon lives inside a rotated+zoomed card, so anchor from the button's
// live client rect rather than CSS coords.
const placeCursorOnButton = (slide, cursor, enable) => {
  const stack = slide.querySelector('.landing-hero-stack');
  if (!stack) return;
  const stackRect = stack.getBoundingClientRect();
  const btnRect = enable.getBoundingClientRect();
  const tipX = btnRect.left + btnRect.width * 0.5 - stackRect.left;
  const tipY = btnRect.top + btnRect.height * 0.5 - stackRect.top;
  const left = `${tipX - CURSOR_TIP_X}px`;
  const top = `${tipY - CURSOR_TIP_Y}px`;
  // Runs every frame: a redundant write here dirties layout for the next frame's rect reads.
  if (cursor.style.left !== left) cursor.style.left = left;
  if (cursor.style.top !== top) cursor.style.top = top;
};

// Cursor approach pose — down-right of the target, so the intro reads as a
// "hand coming in to tap" in one stroke.
const CURSOR_APPROACH_POSE = { opacity: 0, x: 54, y: 48, scale: 1.04, rotate: -8 };

const CURSOR_TRACK_AT = 0.3;
const CURSOR_RETRACT_AT = 1.5;
const CURSOR_RETRACT_DURATION = 0.5;
// Tracking spans exactly the window the cursor is on screen; overrunning it would make this the
// timeline's last child and lengthen tl.duration(), which paces the progress ring.
const CURSOR_TRACK_DURATION = CURSOR_RETRACT_AT + CURSOR_RETRACT_DURATION - CURSOR_TRACK_AT;

const STAGE_DURATION = 5.14;
const STAGE_END_DWELL = 0.4;
const IRIS_TRANSITION = 1.2;
const POST_TRANSITION_DWELL = 0.2;

const beforeAfterStageEls = (slide, stageIndex) => ({
  before: slide.querySelector(`[data-seq="before"][data-stage-index="${stageIndex}"]`),
  after: slide.querySelector(`[data-seq="after"][data-stage-index="${stageIndex}"]`),
  card: slide.querySelector(`[data-seq="card"][data-card-index="${stageIndex}"]`),
  enable: slide.querySelector(`[data-seq="card"][data-card-index="${stageIndex}"] [data-seq="enable"]`),
});

const beforeAfterStageCount = (slide) => slide.querySelectorAll('[data-seq="before"]').length;

// A slide whose content is its own component carries these on that component's root, not on the
// wrapper this controller renders — so look at both. Non-numeric or missing falls back.
const slideNumber = (slide, key, attr, fallback) => {
  const el = slide.dataset[key] ? slide : slide.querySelector(`[${attr}]`);
  const raw = el?.dataset[key]?.trim();
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

// The shipped wipe. `data-iris-duration` (seconds) overrides it per slide; the underline keeps its
// authored share of the beat so the two still read as one gesture.
const IRIS_DURATION = 1.2;
const UNDERLINE_SHARE = 1.1 / IRIS_DURATION;

// Enable arrival → cursor approach → click → iris reveal.
// `enableAt` is the anchor; everything cascades from there.
// Returns the iris-end time (caller adds any post-stage dwell).
const addBeforeAfterClickReveal = (tl, slide, stageIndex, enableAt, { primeCursor = false } = {}) => {
  const cursor = slide.querySelector('[data-seq="cursor"]');
  const { after, card, enable } = beforeAfterStageEls(slide, stageIndex);
  const corner = card?.dataset.corner ?? 'tr';

  // (Enable button is already visible from reset — no fade-in here.)

  if (cursor && enable) {
    // Re-prime cursor for stages 2+ (previous stage left it retracted).
    if (primeCursor) tl.call(() => gsap.set(cursor, CURSOR_APPROACH_POSE), [], enableAt + 0.25);

    // Re-anchored every frame, not once: the button rides CSS transitions (the vacated hero's card
    // is still finishing its migration here) on a clock GSAP's `timeScale` knows nothing about, so a
    // single measurement strands the cursor wherever the button happened to be (UNS-5283).
    const trackButton = () => placeCursorOnButton(slide, cursor, enable);
    tl.to({}, { duration: CURSOR_TRACK_DURATION, onUpdate: trackButton }, enableAt + CURSOR_TRACK_AT);
    tl.to(cursor, { opacity: 1, duration: 0.2, ease: 'power2.out' }, enableAt + 0.35);
    tl.to(cursor, { x: 0, y: 0, rotate: -2, scale: 1, duration: 0.7, ease: 'power3.out' }, enableAt + 0.35);

    // Click: dip, depress, ripple fires via .is-pressed.
    tl.to(cursor, { scale: 0.88, duration: 0.07, ease: 'power2.in' }, enableAt + 1.17);
    tl.to(enable, { scale: 0.96, duration: 0.07, ease: 'power2.in' }, enableAt + 1.17);
    tl.call(() => enable.classList.add('is-pressed'), [], enableAt + 1.21);

    // Release — ripple expands outward as is-enabled takes over.
    tl.to(cursor, { scale: 1.0, duration: 0.2, ease: 'back.out(2.2)' }, enableAt + 1.25);
    tl.to(enable, { scale: 1.0, duration: 0.2, ease: 'back.out(2.2)' }, enableAt + 1.25);
    tl.call(
      () => {
        enable.classList.remove('is-pressed');
        enable.classList.add('is-enabled');
      },
      [],
      enableAt + 1.27,
    );

    // Cursor retracts down-right — doesn't cross the button.
    tl.to(cursor, { x: 30, y: 36, rotate: -8, opacity: 0, duration: CURSOR_RETRACT_DURATION, ease: 'power2.in' }, enableAt + CURSOR_RETRACT_AT);
  } else if (enable) {
    tl.call(() => enable.classList.add('is-enabled'), [], enableAt + 1.27);
  }

  // Card leaves with the cursor so the iris finishes on an unobstructed page.
  if (card) tl.to(card, { opacity: 0, y: 10, filter: 'blur(6px)', duration: 0.5, ease: 'power2.in' }, enableAt + 1.5);

  // Iris opens from this stage's card corner — reads as caused by the click.
  const irisDuration = slideNumber(slide, 'irisDuration', 'data-iris-duration', IRIS_DURATION);
  if (after) tl.to(after, { clipPath: IRIS_OPEN_AT[corner], duration: irisDuration, ease: 'power2.inOut' }, enableAt + 1.29);

  // A thin colour underline sweeps the lower edge in the same beat as the iris,
  // matching its direction. Offsets are derived from the line/clip widths (see
  // `underlineSweepBounds`) so they track the CSS width.
  const underline = slide.querySelector('[data-seq="underline"]');
  if (underline) {
    const { startX, endX } = underlineSweepBounds(corner, underline.getBoundingClientRect().width, underline.parentElement.getBoundingClientRect().width);
    const sweepStart = enableAt + 1.31;
    const sweepDuration = irisDuration * UNDERLINE_SHARE;
    const sweepFadeOut = 0.18;
    tl.set(underline, { x: startX, opacity: 0 }, enableAt + 1.29);
    tl.to(underline, { opacity: 1, duration: 0.14, ease: 'power1.out' }, sweepStart);
    tl.to(underline, { x: endX, duration: sweepDuration, ease: 'power2.inOut' }, sweepStart);
    tl.to(underline, { opacity: 0, duration: sweepFadeOut, ease: 'power1.in' }, sweepStart + sweepDuration - sweepFadeOut);
  }

  return enableAt + 1.29 + irisDuration; // iris transition end
};

// Stages 2+ of multi-stage before-after slides. Stage 0 takes the first-stage
// path (see `addBeforeAfterFirstStage`).
const addBeforeAfterStage = (tl, slide, stageIndex, t0) => {
  const { card } = beforeAfterStageEls(slide, stageIndex);
  if (card) addCardArrival(tl, card, t0 + 0.35);
  addBeforeAfterClickReveal(tl, slide, stageIndex, t0 + 2.2, { primeCursor: true });
};

// Stage-0 intro: card + shimmer → click reveal.
// Returns the stage end time so multi-stage intros can chain.
const addBeforeAfterFirstStage = (tl, slide, t0) => {
  const { card } = beforeAfterStageEls(slide, 0);

  if (card) {
    addCardArrival(tl, card, t0 + 0.35);
    playShimmer(tl, card, t0 + 0.7);
  }

  const irisEnd = addBeforeAfterClickReveal(tl, slide, 0, t0 + 3.3);
  return irisEnd + 0.45; // tail dwell to match the standard stage's post-iris pause
};

const SLIDE_TYPES = {
  'before-after': {
    reset(slide) {
      const cursor = slide.querySelector('[data-seq="cursor"]');
      const n = beforeAfterStageCount(slide);

      if (cursor) gsap.set(cursor, CURSOR_APPROACH_POSE);
      // Just hide it — the reveal sets the correct direction-aware start position.
      const underline = slide.querySelector('[data-seq="underline"]');
      if (underline) gsap.set(underline, { opacity: 0 });

      for (let i = 0; i < n; i++) {
        const { before, after, card, enable } = beforeAfterStageEls(slide, i);
        const corner = card?.dataset.corner ?? 'tr';
        // Stage 0 starts visible (it's the first image shown); stages 2+
        // are hidden and revealed by the prior iris-close.
        if (before) gsap.set(before, { opacity: i === 0 ? 1 : 0, y: 0, filter: 'blur(0px)' });
        if (after) gsap.set(after, { opacity: 1, clipPath: IRIS_CLOSED_AT[corner] });
        if (card) {
          const rot = CORNER_ROTATIONS[corner] ?? 0;
          gsap.set(card, { opacity: 0, scale: 0.92, x: 0, y: 14, rotation: rot, filter: 'blur(8px)' });
        }
        if (enable) {
          gsap.set(enable, { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' });
          enable.classList.remove('is-enabled', 'is-pressed');
        }
      }
    },

    intro(slide, { reducedMotion }) {
      const n = beforeAfterStageCount(slide);
      const tl = gsap.timeline();

      if (reducedMotion) {
        // Fade-only variant of the per-stage sequence: no cursor, no iris tween.
        const RM_STAGE_DURATION = 1.8;
        const fadeIn = { opacity: 1, y: 0, filter: 'blur(0px)' };
        for (let i = 0; i < n; i++) {
          const { before, after, card, enable } = beforeAfterStageEls(slide, i);
          const corner = card?.dataset.corner ?? 'tr';
          const rot = CORNER_ROTATIONS[corner] ?? 0;
          const t0 = i * RM_STAGE_DURATION;

          if (i === 0) {
            if (before) tl.to(before, { ...fadeIn, duration: 0.35, ease: 'power3.out' }, t0);
          } else {
            // Snap previous after closed so this stage's before shows through.
            const prevAfter = beforeAfterStageEls(slide, i - 1).after;
            if (prevAfter) tl.call(() => gsap.set(prevAfter, { clipPath: IRIS_CLOSED_AT[corner] }), [], t0);
            if (before) tl.set(before, { opacity: 1, y: 0, filter: 'blur(0px)' }, t0);
          }
          if (card) tl.to(card, { opacity: 1, scale: 1, x: 0, y: 0, rotation: rot, filter: 'blur(0px)', duration: 0.3, ease: 'power3.out' }, t0 + 0.1);
          if (enable) tl.to(enable, { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.3, ease: 'power3.out' }, t0 + 0.1);

          // Activate mid-stage: snap iris open. No cursor.
          const activateAt = t0 + RM_STAGE_DURATION * 0.5;
          if (enable) tl.call(() => enable.classList.add('is-enabled'), [], activateAt);
          if (after) tl.call(() => gsap.set(after, { clipPath: IRIS_OPEN_AT[corner] }), [], activateAt);
        }
        return tl;
      }

      let t = 0;
      for (let i = 0; i < n; i++) {
        if (i > 0) {
          // Iris collapses toward the next card's corner, sweeping the eye
          // there. The new before (DOM-stacked beneath) flips to opacity 1
          // so it shows through the shrinking aperture.
          const prevAfter = beforeAfterStageEls(slide, i - 1).after;
          const currBefore = beforeAfterStageEls(slide, i).before;
          const currCard = beforeAfterStageEls(slide, i).card;
          const nextCorner = currCard?.dataset.corner ?? 'tr';

          t += STAGE_END_DWELL;
          if (currBefore) tl.set(currBefore, { opacity: 1 }, t);
          if (prevAfter) tl.to(prevAfter, { clipPath: IRIS_CLOSED_AT[nextCorner], duration: IRIS_TRANSITION, ease: 'power2.inOut' }, t);
          t += IRIS_TRANSITION + POST_TRANSITION_DWELL;
        }
        // Stage 0 gets the first-stage intro; later stages use the standard pattern.
        if (i === 0) {
          t = addBeforeAfterFirstStage(tl, slide, t);
        } else {
          addBeforeAfterStage(tl, slide, i, t);
          t += STAGE_DURATION;
        }
      }

      return tl;
    },
  },

  // Single image + 0-4 corner-anchored cards. Each arrives one at a time
  // with a reading gap; a shimmer sweep across the content text marks each arrival.
  'multi-profile-card': {
    reset(slide) {
      const img = slide.querySelector('[data-seq="before"]');
      const cards = slide.querySelectorAll('[data-seq="card"]');
      if (img) gsap.set(img, { opacity: 0, y: 18, filter: 'blur(8px)' });
      cards.forEach((card) => {
        // Clear state from any prior/interrupted sweep.
        card.classList.remove('is-spotlit', 'is-dimmed');
        card.style.removeProperty('--shimmer-alpha');
        card.style.removeProperty('--shimmer-progress');
        const rot = CORNER_ROTATIONS[card.dataset.corner] ?? 0;
        gsap.set(card, { opacity: 0, scale: 0.92, x: 0, y: 14, rotation: rot, filter: 'blur(8px)' });
      });
    },

    intro(slide, { reducedMotion }) {
      const img = slide.querySelector('[data-seq="before"]');
      const cards = Array.from(slide.querySelectorAll('[data-seq="card"]'));
      const tl = gsap.timeline();

      if (reducedMotion) {
        if (img) tl.to(img, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.35, ease: 'power3.out' });
        cards.forEach((card) => {
          const rot = CORNER_ROTATIONS[card.dataset.corner] ?? 0;
          gsap.set(card, { opacity: 1, scale: 1, x: 0, y: 0, rotation: rot, filter: 'blur(0px)' });
        });
        return tl;
      }

      if (img) tl.to(img, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.35, ease: 'power3.out' }, 0.04);

      const FIRST_CARD_AT = 0.45;
      // `data-card-gap` (seconds) overrides the stagger for one slide; the default reads as a
      // slow reveal, which is wrong when several cards are the beat rather than an accent.
      // Also read off a descendant: a slide whose content is its own component carries the
      // attribute on that component's root, not on the wrapper this controller renders.
      const arrivalGap = slideNumber(slide, 'cardGap', 'data-card-gap', MULTI_CARD_ARRIVAL_GAP);
      cards.forEach((card, i) => {
        const t = FIRST_CARD_AT + i * arrivalGap;
        addCardArrival(tl, card, t);
        playShimmer(tl, card, t + 0.35);
      });

      return tl;
    },
  },

  // Fullbleed looping muted video. The video element itself owns playback;
  // the strategy just fades the stage in and (re)starts play on activate /
  // pauses on deactivate, so inactive slides aren't decoding frames.
  video: {
    reset(slide) {
      const video = slide.querySelector('video');
      if (video) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch {
          // Some browsers throw if metadata isn't loaded yet — safe to ignore.
        }
      }
      const bg = slide.querySelector('[data-seq="before"]');
      if (bg) gsap.set(bg, { opacity: 0, y: 18, filter: 'blur(8px)' });
    },

    intro(slide, { reducedMotion }) {
      const bg = slide.querySelector('[data-seq="before"]');
      const video = slide.querySelector('video');
      const tl = gsap.timeline();

      if (reducedMotion) {
        if (bg) gsap.set(bg, { opacity: 1, y: 0, filter: 'blur(0px)' });
        startVideoPlayback(video);
        return tl;
      }

      if (bg) tl.to(bg, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.35, ease: 'power3.out' }, 0.04);
      tl.call(() => startVideoPlayback(video), [], 0);
      return tl;
    },
  },
};

const strategyFor = (slide) => {
  const strategy = SLIDE_TYPES[slide.dataset.slideType];
  if (!strategy) {
    throw new Error(`feature_showcase: unknown data-slide-type "${slide.dataset.slideType}"`);
  }
  return strategy;
};

export default class extends Controller {
  static targets = ['pill', 'slide', 'rail'];
  static values = {
    interval: { type: Number, default: 6000 },
    active: { type: Number, default: 0 },
    // loop: false parks the carousel on the last slide instead of wrapping — used by the
    // promo video so a fixed-length outro can cover the showcase without it restarting.
    loop: { type: Boolean, default: true },
    // speed: timeScale applied to each slide's choreography. >1 compresses the animations
    // proportionally — used by the promo video to fit its 30s budget.
    speed: { type: Number, default: 1 },
    // endDwell: ms held on the final "after" state before advancing/replaying (900 matches
    // the legacy POST_INTRO_DWELL). Bumped for single-slide demos that linger on the result.
    endDwell: { type: Number, default: 900 },
  };

  connect() {
    this.__paused = false;
    this.__suspended = false;
    this.__inView = false;
    this.__resumeVideo = null;
    this.__advanceRemainingMs = null;
    this.__timeline = null;
    this.__progressTween = null;
    this.__advanceTimer = null;
    this.__started = false;

    // Stimulus can connect before the first style recalc, and a class written before one has no
    // before-change style to transition from — every CSS phase of the beat would compute straight to
    // its end pose. Flushing here is what makes the `is-active` below transition at all (UNS-5271).
    void this.element.offsetWidth;
    this.__resetAll();
    this.__markActivePill(this.activeValue);
    this.__setupObserver();
    this.__setupCardScaleObserver();
    this.__updateScrollAffordance();

    this.__onVisibility = () => this.__updateRunState();
    document.addEventListener('visibilitychange', this.__onVisibility);
    this.__onResize = () => this.railScroll();
    window.addEventListener('resize', this.__onResize);
  }

  disconnect() {
    if (this.__observer) this.__observer.disconnect();
    if (this.__cardScaleObserver) this.__cardScaleObserver.disconnect();
    if (this.__timeline) this.__timeline.kill();
    if (this.__progressTween) this.__progressTween.kill();
    if (this.__advanceTimer) clearTimeout(this.__advanceTimer);
    if (this.__affordanceFrame) cancelAnimationFrame(this.__affordanceFrame);
    document.removeEventListener('visibilitychange', this.__onVisibility);
    window.removeEventListener('resize', this.__onResize);
    this.__detachVideoEndedListener();
  }

  // actions

  select(event) {
    const index = Number(event.currentTarget.dataset.index);
    if (Number.isNaN(index)) return;
    if (index === this.activeValue) return;
    this.__pauseAutoCycle();
    this.__activate(index);
  }

  keydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    this.__pauseAutoCycle();

    const count = this.pillTargets.length;
    const current = this.activeValue;
    let next = current;
    if (event.key === 'ArrowLeft') next = (current - 1 + count) % count;
    if (event.key === 'ArrowRight') next = (current + 1) % count;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = count - 1;

    this.pillTargets[next].focus();
    this.__activate(next);
  }

  // rAF-throttled affordance refresh. Both rail scroll (rapid during active-pill
  // auto-centering) and window resize route through here to coalesce bursts.
  railScroll() {
    if (this.__affordanceFrame) return;
    this.__affordanceFrame = requestAnimationFrame(() => {
      this.__affordanceFrame = null;
      this.__updateScrollAffordance();
    });
  }

  // private

  get __reducedMotion() {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  __pauseAutoCycle() {
    this.__paused = true;
    if (this.__advanceTimer) {
      clearTimeout(this.__advanceTimer);
      this.__advanceTimer = null;
    }
    // Freeze bar mid-fill so the user sees how far rotation had progressed.
    if (this.__progressTween) this.__progressTween.pause();
  }

  // Stays connected for the controller's lifetime: the first intersection starts the
  // carousel; later enter/leave events suspend/resume it so nothing animates off-screen.
  __setupObserver() {
    this.__observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.__inView = entry.isIntersecting;
          if (entry.isIntersecting && !this.__started) {
            this.__started = true;
            this.__activate(this.activeValue);
            return;
          }
          this.__updateRunState();
        });
      },
      { threshold: 0.25, rootMargin: '0px 0px -40px 0px' },
    );
    this.__observer.observe(this.element);
  }

  // Single decision point for both the observer and the visibility listener.
  __updateRunState() {
    if (!this.__started) return;
    if (this.__inView && !document.hidden) this.__resume();
    else this.__suspend();
  }

  // Off-screen / backgrounded: freeze all main-thread work. Distinct from __paused,
  // which is the permanent manual pause and must survive resume.
  __suspend() {
    if (this.__suspended) return;
    this.__suspended = true;
    this.__advanceRemainingMs = null;
    if (this.__advanceTimer) {
      clearTimeout(this.__advanceTimer);
      this.__advanceTimer = null;
      this.__advanceRemainingMs = Math.max(0, this.__advanceDueAt - Date.now());
    }
    if (this.__timeline) this.__timeline.pause();
    if (this.__progressTween) this.__progressTween.pause();
    const video = this.slideTargets[this.activeValue]?.querySelector('video');
    this.__resumeVideo = video && !video.paused ? video : null;
    video?.pause();
  }

  // Back on-screen / foregrounded: pick the beat up where it stopped rather than re-running
  // __activate. A suspend only freezes GSAP — the CSS half of a beat keeps its own clock — so a
  // replay from the top played against a pose the CSS had already spent: the hero sat blank for the
  // length of the arrival lead, and the vacated ghosts flashed back in before hiding (UNS-5281).
  __resume() {
    if (!this.__suspended) return;
    this.__suspended = false;

    const video = this.__resumeVideo;
    const advanceMs = this.__advanceRemainingMs;
    this.__resumeVideo = null;
    this.__advanceRemainingMs = null;
    if (this.__paused) return;

    if (this.__timeline) this.__timeline.resume();
    if (this.__progressTween) this.__progressTween.resume();
    startVideoPlayback(video);
    if (advanceMs !== null) this.__scheduleAdvance(advanceMs);
  }

  // GSAP is only half of a beat. The vacated hero's whole arrival is CSS transitions delayed off
  // `.is-active`, and toggling a class an element already carries restarts nothing — so a slide that
  // has held it since connect (its CSS clock already spent, off-screen or otherwise) would sit at its
  // finished pose while the cursor replays from zero, permanently out of step.
  //
  // Transitions are suppressed for the snap back rather than just removing the class: a bare removal
  // starts a reverse transition, whose value at that instant is still the end pose, so re-adding the
  // class changes nothing. The two writes are one task, so the base pose is flushed but never painted.
  __restartCssBeat(slide) {
    if (!slide?.classList.contains('is-active')) return;

    slide.classList.add('fsh-beat-reset');
    slide.classList.remove('is-active');
    void slide.offsetWidth;
    slide.classList.remove('fsh-beat-reset');
    slide.classList.add('is-active');
    // A MutationObserver sees both class writes in one callback and cannot tell the beat restarted;
    // anything keyed on the beat (vacated_stage_controller's well) re-arms off this instead.
    slide.dispatchEvent(new CustomEvent('showcase:beat-restart', { bubbles: true }));
  }

  __resetAll() {
    this.slideTargets.forEach((slide, i) => {
      this.__setSlideActive(slide, i === this.activeValue);
    });
  }

  __setSlideActive(slide, isActive, { reset = true } = {}) {
    slide.classList.toggle('is-active', isActive);
    slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    if (reset) strategyFor(slide).reset(slide);
  }

  __markActivePill(index) {
    this.pillTargets.forEach((pill, i) => {
      const isActive = i === index;
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      pill.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  __activate(index) {
    if (this.__timeline) {
      this.__timeline.kill();
      this.__timeline = null;
    }
    if (this.__progressTween) {
      this.__progressTween.kill();
      this.__progressTween = null;
    }
    if (this.__advanceTimer) {
      clearTimeout(this.__advanceTimer);
      this.__advanceTimer = null;
    }
    // Reset to 0 here so __runProgress (called again mid-slide, e.g. on outroOnEnded) continues
    // from the bar's current value rather than jumping back.
    this.pillTargets.forEach((pill) => pill.style.setProperty('--showcase-progress', '0'));

    const count = this.slideTargets.length;
    const clamped = ((index % count) + count) % count;
    this.activeValue = clamped;
    this.__markActivePill(clamped);
    this.__updateFrameUrl(this.slideTargets[clamped]);
    this.__scrollActivePillIntoView(clamped);
    this.__updateScrollAffordance();

    // The incoming slide may already carry `is-active` — connect marks one so the stage isn't blank
    // before the observer fires, and its CSS beat has been running ever since, on a clock GSAP's
    // start here knows nothing about. On a fresh slide this is a no-op.
    this.__restartCssBeat(this.slideTargets[clamped]);

    // Only the incoming slide is reset to its start pose. Resetting the outgoing
    // slide here would snap its imagery from the revealed "after" back to the
    // "before" while it's still mid-fade-out (visible ~0.45s), reading as a
    // flicker; instead it keeps its end pose and is reset on its next activation.
    // A playing video is the one bit of state we can't defer — pause it now so
    // hidden slides aren't left decoding frames.
    this.slideTargets.forEach((slide, i) => {
      const isActive = i === clamped;
      this.__setSlideActive(slide, isActive, { reset: isActive });
      if (!isActive) slide.querySelector('video')?.pause();
    });

    this.__runIntro(this.slideTargets[clamped]);
  }

  // Keep the active pill centered in the horizontally-scrollable rail. Scrolls only
  // the rail (scrollBy on its parent), so it never nudges the page vertically.
  __scrollActivePillIntoView(index) {
    const pill = this.pillTargets[index];
    const rail = pill?.parentElement;
    if (!pill || !rail) return;
    const railRect = rail.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    const delta = pillRect.left + pillRect.width / 2 - (railRect.left + railRect.width / 2);
    if (Math.abs(delta) < 1) return;
    rail.scrollBy({ left: delta, behavior: this.__reducedMotion ? 'auto' : 'smooth' });
  }

  // Toggle the edge fade/chevron at each end based on how far the rail can still scroll.
  // No-op (both hidden) when the pills fit, so it self-disables on wide viewports.
  __updateScrollAffordance() {
    if (!this.hasRailTarget) return;
    const rail = this.railTarget;
    const wrap = rail.parentElement;
    if (!wrap) return;
    const EDGE = 2;
    const max = rail.scrollWidth - rail.clientWidth;
    wrap.toggleAttribute('data-scroll-left', rail.scrollLeft > EDGE);
    wrap.toggleAttribute('data-scroll-right', rail.scrollLeft < max - EDGE);
  }

  // Scale each fixed-design-width demo card down to fit its float container.
  // Computed here (not in CSS) because the ratio is float-content-width ÷
  // design-width, and deriving that in calc() means dividing two lengths — which
  // Firefox rejects, so a CSS-only `zoom: calc(100cqw/…)` resolved to 1 there
  // (card overflowed full-size) and Safari mis-scaled it. The card SCSS feeds
  // this unitless number straight into `transform: scale()`, which is portable.
  //
  // A ResizeObserver (not a one-shot/resize handler) keeps it correct through
  // every reflow that changes a float's width without a window resize:
  // scroll-into-view, web-font load, slide activation. `transform` doesn't
  // reflow and `container-type: size` decouples the float from its inner, so
  // writing --card-zoom never resizes the observed element — no feedback loop.
  __setupCardScaleObserver() {
    const cards = this.element.querySelectorAll('.landing-hero-float--card');
    if (typeof ResizeObserver === 'undefined') {
      cards.forEach((card) => this.__scaleCard(card));
      return;
    }
    this.__cardScaleObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => this.__scaleCard(entry.target));
    });
    cards.forEach((card) => this.__cardScaleObserver.observe(card));
  }

  __scaleCard(card) {
    const styles = getComputedStyle(card);
    const design = parseFloat(styles.getPropertyValue('--card-design-width')) || 348;
    const contentWidth = card.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    if (contentWidth > 0) card.style.setProperty('--card-zoom', contentWidth / design);
  }

  // Each slide can carry its own `data-frame-url`; mirror it into the browser-frame
  // address bar so a multi-site showcase shows the right URL per slide.
  __updateFrameUrl(slide) {
    const url = slide?.dataset.frameUrl;
    if (!url) return;
    const el = this.element.querySelector('.landing-browser-url-text');
    if (el) el.textContent = url;
  }

  // Tweens the active pill's progress ring to 100 over `durationMs`.
  // Callable mid-slide to re-pace for a follow-on phase (e.g. post-ended outro).
  __runProgress(durationMs) {
    if (this.__progressTween) this.__progressTween.kill();
    if (this.__paused || this.__reducedMotion) return;

    const activePill = this.pillTargets[this.activeValue];
    if (!activePill) return;

    const state = { p: parseFloat(activePill.style.getPropertyValue('--showcase-progress')) || 0 };
    this.__progressTween = gsap.to(state, {
      p: 100,
      duration: durationMs / 1000,
      ease: 'none',
      onUpdate: () => activePill.style.setProperty('--showcase-progress', String(state.p)),
    });
  }

  // Advance timer scales with intro duration so longer choreographies aren't cut off.
  __runIntro(slide) {
    const reducedMotion = this.__reducedMotion;
    this.__timeline = strategyFor(slide).intro(slide, { reducedMotion });
    if (this.__timeline) this.__timeline.timeScale(this.speedValue);

    // duration() ignores timeScale, so divide to get the effective length.
    const introMs = this.__timeline ? (this.__timeline.duration() / this.speedValue) * 1000 : 0;
    // Hold on the final "after" state before advancing; `endDwell` lengthens it for
    // single-slide demos that want a longer pause before replaying.
    const advanceMs = Math.max(this.intervalValue, introMs + this.endDwellValue);
    this.__runProgress(advanceMs);
    this.__scheduleAdvance(reducedMotion ? 5000 : advanceMs);
  }

  __scheduleAdvance(ms) {
    this.__attachVideoEndedListener();
    if (this.__paused) return;
    // Non-looping video slides prefer `ended`; `ms` is the fallback in case `ended` never fires.
    // If `ended` already fired (e.g. rescheduling after an outro), `ms` is the real schedule.
    const pendingVideo = this.__endedVideo;
    if (pendingVideo && !pendingVideo.loop && !pendingVideo.ended) {
      ms = this.__videoFallbackMs(pendingVideo);
    }
    this.__advanceDueAt = Date.now() + ms;
    this.__advanceTimer = setTimeout(() => {
      this.__advanceTimer = null;
      if (this.__paused) return; // user may have paused between schedule and fire
      this.__advanceToNext();
    }, ms);
  }

  // Safety-net duration for a non-looping video whose `ended` event never
  // arrives: the clip's own length plus a buffer when metadata is loaded, else
  // a generous constant (duration is unknown under preload="none").
  __videoFallbackMs(video) {
    const FALLBACK_BUFFER_MS = 2000;
    const FALLBACK_UNKNOWN_MS = 30000;
    return Number.isFinite(video.duration) && video.duration > 0 ? video.duration * 1000 + FALLBACK_BUFFER_MS : FALLBACK_UNKNOWN_MS;
  }

  __advanceToNext() {
    const next = (this.activeValue + 1) % this.slideTargets.length;
    // promo_extension_video_controller times its outro off this event.
    if (next === 0 && !this.loopValue) {
      this.dispatch('complete');
      return;
    }
    this.__activate(next);
  }

  // For non-looping video slides, advance on `ended` rather than the interval.
  // If the strategy defines `outroOnEnded`, run that timeline first then reschedule.
  __attachVideoEndedListener() {
    this.__detachVideoEndedListener();
    const slide = this.slideTargets[this.activeValue];
    if (!slide) return;
    const video = slide.querySelector('video');
    if (!video || video.loop) return;
    const strategy = strategyFor(slide);
    const reducedMotion = this.__reducedMotion;
    this.__endedVideo = video;
    this.__endedHandler = () => {
      if (this.__advanceTimer) {
        clearTimeout(this.__advanceTimer);
        this.__advanceTimer = null;
      }
      // Fire outroOnEnded regardless of __paused — it's part of the slide's presentation.
      // The subsequent advance still respects __paused so manual control isn't overridden.
      if (typeof strategy.outroOnEnded === 'function') {
        if (this.__timeline) this.__timeline.kill();
        this.__timeline = strategy.outroOnEnded(slide, { reducedMotion });
        const outroMs = this.__timeline ? this.__timeline.duration() * 1000 : 0;
        const POST_OUTRO_DWELL = 1200;
        // Re-pace the bar over outro+dwell so it reaches 100% as the slide changes.
        this.__runProgress(outroMs + POST_OUTRO_DWELL);
        this.__scheduleAdvance(outroMs + POST_OUTRO_DWELL);
        return;
      }
      if (this.__paused) return;
      this.__advanceToNext();
    };
    video.addEventListener('ended', this.__endedHandler, { once: true });
  }

  __detachVideoEndedListener() {
    if (this.__endedVideo && this.__endedHandler) {
      this.__endedVideo.removeEventListener('ended', this.__endedHandler);
    }
    this.__endedVideo = null;
    this.__endedHandler = null;
  }
}
